/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import React, { useState, useEffect, useRef } from 'react';
import { GatsbyImage } from "gatsby-plugin-image";
import IconPackage from 'xiv-fanfes-2021';
import { useLocalization } from 'gatsby-theme-i18n';
import styled from '@emotion/styled';
import RoundButton from './../RoundButton'

import * as breakpoint from '../../styles/breakpoints.module.scss';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

const Popup = ({ logo, brand, desc, url, campaignUrl, setModal, isOpen }) => {
  const { t } = useLocalization();
  const [ fade , setFade ] = useState(false);
  let fadeTimer = useRef(null);
  const closePopup = (e) => {
    e.preventDefault();
    setFade(false);
    setTimeout(() => {
      setModal(false)
    },300);
  }
  
  useEffect(() => {
    if(isOpen)
      fadeTimer.current = setTimeout(() => {
        setFade(true);
      },50);
    return () => {
      clearTimeout(fadeTimer.current);
      fadeTimer.current = null
    };
  }, [isOpen]);

  return (<React.Fragment>
    <Overlay className={fade ? 'active' : ''}/>
    <CardWrapper onClick={closePopup}>
      <SimpleBar id="modal-scroll">
        <Card className={fade ? 'active' : ''} onClick={(e) => {
          e.stopPropagation();
        }}>
          <BtnClose href="#" onClick={closePopup}>
            <IconPackage.Icon type={'Plus'} />
            <span>{t('sponsors.modal.close')}</span>
          </BtnClose>
          <Logo><GatsbyImage image={logo} alt={brand} title={brand}/></Logo>
          <Brand>{brand}</Brand>
          <Desc>{desc}</Desc >
          <div>
            <Button color={'#4E4E50'} size={'large'} href={url}>
              {t('sponsors.modal.ditail.button.label')}
            </Button>
            {campaignUrl &&
              <Button color={'#F50057'} size={'large'} href={campaignUrl} css={css`
                  margin-left:32px;
                  @media only screen and (max-width: ${breakpoint.smMax}){
                    margin-left:16px;
                  }
                  @media only screen and (max-width: ${breakpoint.xsMax}){
                    margin-left:0;
                    margin-top:8px
                  }
              `}>{t('sponsors.modal.campaign.button.label')}
              </Button>
            }
          </div>
        </Card>
      </SimpleBar>
    </CardWrapper>
  </React.Fragment>);
}
 
export default Popup;

const Overlay = styled.div`
  background: rgba(28, 28, 29, 0.9);
  position:absolute;
  top: 0;
  left:0;
  bottom:0;
  right:0;
  opacity:0;
  transition: opacity .3s;

  &.active{
    opacity: 1;
  }
`

const CardWrapper = styled.div`
  position:absolute;
  top: 0;
  left:0;
  bottom:0;
  right:0;
  overflow:auto;
  line-height:100vh;
  text-align:center;

  #modal-scroll{
    height:100%;
    width:100%;

    .simplebar-track.simplebar-vertical{
      width:7px;
    }
  }
`

const Card = styled.div`

  margin:0 auto;
  display:inline-block;
  vertical-align:middle;
  line-height:1em;
  
  position:relative;
  width:928px;
  max-width: calc(100% - 144px);
  transition: all .3s;
  opacity:0;
  transform: translateY(10%);
  
  background: #FFFFFF;

  box-shadow: 0px 2px 2px -2px rgba(0, 0, 0, 0.05), 0px 6px 6px -6px rgba(0, 0, 0, 0.03), 0px 16px 14px -14px rgba(0, 0, 0, 0.03);
  border-radius: 12px;

  text-align: center;

  padding:96px;

  &.active {
    opacity: 1;
    transform: translateY(0%);
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    max-width: calc(100% - 112px);
    padding:64px;
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    max-width: calc(100% - 48px);
    margin-top:24px;
    margin-bottom:24px;
    padding:48px 32px 32px;
  }
  
`

const BtnClose = styled.div`
  display:block;
  width:64px;
  height:64px;
  position:absolute;
  top:16px;
  right:16px;
  color:#CACACD;
  overflow:hidden;
  z-index:5;
  cursor: pointer;

  > svg{
    width:100%;
    height:100%;
    transform: rotate(45deg);
  }
  > span{
    text-indent: -9999px;
  }

  &:hover{
    color:#4E4E50;
  }

  @media only screen and (max-width: ${breakpoint.smMax}){
    width:48px;
    height:48px;
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    width:32px;
    height:32px;
  }
`

const Logo = styled.div`
  width: 352px;
  max-width: 100%;
  margin:0 auto 16px;
`

const Brand = styled.div`
  color: #2C2C2E;
  font-weight: 900;
  font-size: 24px;
  line-height: 36px;

  text-align: center;
  letter-spacing: 0.03em;
  margin-bottom:54px;

  @media only screen and (max-width: ${breakpoint.smMax}){
    font-size: 16px;
    line-height: 24px;
    margin-bottom:32px;
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    font-size: 14px;
    line-height: 24px;
    margin-bottom:16px;
  }
`

const Desc = styled.div`
  font-size: 16px;
  line-height: 28px;

  color: #444448;
  margin-bottom:74px;
  text-align:left;

  @media only screen and (max-width: ${breakpoint.smMax}){
    font-size: 14px;
    line-height: 24px;
    margin-bottom:32px;
  }

  @media only screen and (max-width: ${breakpoint.xsMax}){
    font-size: 12px;
    line-height: 20px;
    margin-bottom:16px;
  }

`

const Button = styled(RoundButton)`
  font-size: 28px;
  line-height: 32px;
  ${props => props.color === "#4E4E50" ? `padding:14px 30px;` : `padding:16px 32px;`}
  height:auto;
  @media only screen and (max-width: ${breakpoint.smMax}){
    height:auto;
    font-size: 17px;
    line-height: 24px;
    ${props => props.color === "#4E4E50" ? `padding:10px 22px;` : ` padding:12px 24px;`}
    border-radius: 26px;
  }
  @media only screen and (max-width: ${breakpoint.xsMax}){
    height:auto;
    width:100%;
    ${props => props.color === "#4E4E50" ? `padding:6px  14px;` : `padding:8px 16px;`}
    font-size: 14px;
    line-height: 20px;
  }
`
